import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, StatusBadge } from './ui.tsx';

/**
 * Card readers at the counter: the till hands the amount to a connected reader (Stripe Terminal or
 * the simulator), the customer taps or inserts the card, and the till learns the result. ERP PRO
 * never sees the card: the reader and the provider handle it.
 */

export interface ReaderOption {
  id: string;
  label: string;
  online: boolean;
  last_error: string | null;
  test: boolean;
}

interface ReaderPayment {
  id: string;
  ref: string;
  amount: number;
  currency: string;
  state: 'open' | 'processing' | 'succeeded' | 'cancelled' | 'expired';
  error: string | null;
  method: string | null;
  reader: { id: string; label: string } | null;
  test: boolean;
  simulator: boolean;
}

const LAST = 'erp.reader';
const lastReader = {
  get: () => {
    try {
      return localStorage.getItem(LAST);
    } catch {
      return null;
    }
  },
  set: (id: string) => {
    try {
      localStorage.setItem(LAST, id);
    } catch {
      /* private window: the till just asks again */
    }
  },
};

/** The card readers this person may use; empty when there are none or card payments are not set up. */
export function useReaders() {
  const { can } = useSession();
  const allowed = can('payments.reader');
  const [readers, setReaders] = useState<ReaderOption[]>([]);
  useEffect(() => {
    if (!allowed) return;
    // A business without card payments simply has no readers: nothing to show.
    action<ReaderOption[]>('payments.readers', {}).then(setReaders, () => setReaders([]));
  }, [allowed]);
  return readers;
}

/** The reader choice and the button that sends the amount to it, shown with the card method. */
export function ReaderPay({
  readers,
  amount,
  purpose,
  description,
  disabled,
  onPaid,
}: {
  readers: ReaderOption[];
  amount: number;
  purpose: { module: string; record: string };
  description: string;
  disabled?: boolean;
  onPaid: (ref: string, amount: number) => void;
}) {
  const [chosen, setChosen] = useState(() => lastReader.get());
  const [open, setOpen] = useState(false);
  const reader = readers.find((r) => r.id === chosen) ?? readers[0]!;
  return (
    <div className="rd-pay">
      {readers.length > 1 ? (
        <label className="rd-pick">
          <span className="muted small">{t('rd.reader')}</span>
          <select
            className="input"
            value={reader.id}
            onChange={(e) => {
              setChosen(e.target.value);
              lastReader.set(e.target.value);
            }}
          >
            {readers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <Button variant="primary" size="xl" icon="send" className="w-full" disabled={disabled || amount <= 0} onClick={() => setOpen(true)}>
        {readers.length > 1 ? t('rd.send', { amount: money(amount) }) : t('rd.send_to', { amount: money(amount), reader: reader.label })}
      </Button>
      {open ? (
        <ReaderDialog
          reader={reader}
          amount={amount}
          purpose={purpose}
          description={description}
          onClose={() => setOpen(false)}
          onPaid={(ref) => {
            setOpen(false);
            onPaid(ref, amount);
          }}
        />
      ) : null}
    </div>
  );
}

/** Waits for the card on the reader: it asks every two seconds, and can be stopped. */
function ReaderDialog({
  reader,
  amount,
  purpose,
  description,
  onClose,
  onPaid,
}: {
  reader: ReaderOption;
  amount: number;
  purpose: { module: string; record: string };
  description: string;
  onClose: () => void;
  onPaid: (ref: string) => void;
}) {
  const { fail, toast } = useUi();
  const [p, setP] = useState<ReaderPayment | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = useRef(false);
  const paid = useRef(onPaid);
  paid.current = onPaid;

  const send = async () => {
    setBusy(true);
    setTrouble(null);
    try {
      setP(await action<ReaderPayment>('payments.reader_start', { reader: reader.id, amount, description, label: description, purpose }));
    } catch (err) {
      setTrouble(err instanceof Error ? err.message : t('error.generic'));
    }
    setBusy(false);
  };
  useEffect(() => {
    lastReader.set(reader.id);
    void send();
    // Sent once when the dialog opens; "Try again" sends again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waiting = !!p && (p.state === 'open' || p.state === 'processing');
  useEffect(() => {
    if (!p) return;
    if (p.state === 'succeeded' && !done.current) {
      done.current = true;
      toast(t('rd.paid_toast', { method: p.method ?? t('pay.method_card') }));
      paid.current(p.ref);
    }
  }, [p, toast]);
  useEffect(() => {
    if (!waiting || p?.error) return;
    const id = window.setInterval(() => {
      action<ReaderPayment>('payments.reader_status', { id: p!.id }).then(setP, () => {});
    }, 2000);
    return () => window.clearInterval(id);
  }, [waiting, p?.id, p?.error]);

  const stop = async () => {
    if (!p || !waiting) return onClose();
    setBusy(true);
    try {
      const after = await action<ReaderPayment>('payments.reader_cancel', { id: p.id });
      setP(after);
      if (after.state !== 'succeeded') onClose();
    } catch (err) {
      fail(err);
    }
    setBusy(false);
  };
  const play = async (outcome: 'approve' | 'decline') => {
    if (!p) return;
    setBusy(true);
    try {
      setP(await action<ReaderPayment>('payments.reader_simulate', { id: p.id, outcome }));
    } catch (err) {
      fail(err);
    }
    setBusy(false);
  };

  const ended = p && (p.state === 'cancelled' || p.state === 'expired');
  // The simulator's own decline, said in the person's language; providers' words are shown as they come.
  const said = p?.error === 'The card was declined.' ? t('rd.declined') : (p?.error ?? null);
  const problem = trouble ?? (ended ? t('rd.ended') : said);
  return (
    <Dialog
      title={t('rd.title', { reader: reader.label })}
      onClose={() => void stop()}
      size="sm"
      footer={
        <>
          <Button variant="ghost" icon="x" busy={busy && waiting} onClick={() => void stop()}>
            {waiting ? t('rd.stop') : t('common.close')}
          </Button>
          {problem ? (
            <Button variant="primary" icon="repeat" busy={busy && !waiting} onClick={() => void send()} data-autofocus>
              {t('rd.again')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="rd-wait" aria-live="polite">
        <span className={`rd-icon${problem ? ' bad' : p?.state === 'succeeded' ? ' ok' : ''}`} aria-hidden="true">
          <Icon name={problem ? 'alert' : p?.state === 'succeeded' ? 'checkCircle' : 'card'} size={36} />
        </span>
        <strong className="rd-amount">{money(amount)}</strong>
        {problem ? (
          <p className="rd-msg bad" role="alert">
            {problem}
          </p>
        ) : !p ? (
          <p className="rd-msg muted">{t('rd.sending')}</p>
        ) : p.state === 'processing' ? (
          <p className="rd-msg">{t('rd.processing')}</p>
        ) : p.state === 'succeeded' ? (
          <p className="rd-msg">{t('rd.paid', { method: p.method ?? t('pay.method_card') })}</p>
        ) : (
          <p className="rd-msg">{t('rd.present', { reader: reader.label })}</p>
        )}
        {p?.test && waiting ? (
          <div className="rd-test">
            <StatusBadge tone="warn" icon="alert" label={t('rd.test_reader')} />
            <span className="muted small">{p.simulator ? t('rd.test_hint') : t('rd.test_hint_stripe')}</span>
            <span className="rd-test-row">
              <Button size="sm" icon="check" disabled={busy} onClick={() => void play('approve')}>
                {t('rd.test_approve')}
              </Button>
              {p.simulator ? (
                <Button size="sm" icon="x" disabled={busy} onClick={() => void play('decline')}>
                  {t('rd.test_decline')}
                </Button>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
